import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PrimaryButton, SecondaryButton } from '@/lib';
import { useCreateTenant } from './queries';

const PLANS = ['trial', 'pro', 'enterprise'] as const;

export function TenantNew() {
  const navigate = useNavigate();
  const create = useCreateTenant();
  const [displayName, setDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [plan, setPlan] = useState<(typeof PLANS)[number]>('trial');
  const [region, setRegion] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        display_name: displayName,
        contact_email: contactEmail,
        plan,
        region: region || undefined,
      },
      {
        onSuccess: (data) => {
          setInviteToken(data.admin_invite_token ?? null);
          setTenantId(data.tenant.id);
        },
      },
    );
  };

  if (inviteToken && tenantId) {
    const link = `${window.location.origin}/invite?token=${inviteToken}`;
    return (
      <div className="mx-auto flex max-w-2xl flex-col gap-sp-4">
        <header>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Tenant provisioned
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
            Hand off the admin invite
          </h1>
        </header>
        <div className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6">
          <p className="text-fs-body text-gray-700">
            The new tenant <span className="font-mono">{tenantId}</span> is
            active. Send the invitation link below to{' '}
            <span className="font-mono">{contactEmail}</span>:
          </p>
          <pre className="mt-sp-3 break-all rounded-r-default border border-gray-300 bg-gray-100 p-sp-3 font-mono text-fs-meta">
            {link}
          </pre>
          <div className="mt-sp-3 flex items-center gap-sp-3">
            <PrimaryButton
              label="Copy link"
              onClick={() => navigator.clipboard?.writeText(link)}
            />
            <SecondaryButton
              label="Back to tenants"
              onClick={() => navigate({ to: '/radiant/tenants' })}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-sp-4">
      <header>
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Radiant superadmin
        </p>
        <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">New tenant</h1>
      </header>
      <form
        onSubmit={submit}
        className="flex flex-col gap-sp-4 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      >
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Display name
          <input
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Acme Manufacturing"
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Contact email
          <input
            required
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="ops@acme.com"
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
          />
        </label>
        <fieldset className="flex flex-col gap-sp-1">
          <legend className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Plan
          </legend>
          {PLANS.map((p) => (
            <label key={p} className="inline-flex items-center gap-sp-2 text-fs-body text-gray-900">
              <input
                type="radio"
                name="plan"
                checked={plan === p}
                onChange={() => setPlan(p)}
              />
              {p}
            </label>
          ))}
        </fieldset>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Region (optional)
          <input
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="us-east"
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
          />
        </label>
        <div className="flex items-center gap-sp-3">
          <PrimaryButton
            type="submit"
            label={create.isPending ? 'Provisioning…' : 'Provision tenant'}
            disabled={create.isPending}
          />
          <SecondaryButton
            type="button"
            label="Cancel"
            onClick={() => navigate({ to: '/radiant/tenants' })}
          />
          {create.isError ? (
            <span role="alert" className="text-fs-meta text-critical">
              {String(create.error)}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
